import React, { useState, useEffect } from 'react';
import {
  FileText,
  Euro,
  Users,
  Calendar,
  Download,
  Upload,
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  CreditCard,
  Building,
  UserCheck,
  Briefcase
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Tabs del panel
const TABS = [
  { id: 'facturas', label: 'Facturas', icon: FileText },
  { id: 'contabilidad', label: 'Contabilidad', icon: Euro },
  { id: 'rrhh', label: 'RRHH', icon: Users },
  { id: 'nominas', label: 'Nóminas', icon: CreditCard }
];

// Estados de factura
const INVOICE_STATUS = {
  pending: { label: 'Pendiente', color: 'yellow' },
  paid: { label: 'Pagada', color: 'green' },
  overdue: { label: 'Vencida', color: 'red' },
  draft: { label: 'Borrador', color: 'gray' }
};

// Componente de estado badge
const StatusBadge = ({ status }) => {
  const config = INVOICE_STATUS[status] || INVOICE_STATUS.draft;
  const colors = {
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-50 text-gray-700'
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors[config.color]}`}>
      {config.label}
    </span>
  );
};

// Componente de factura
const InvoiceRow = ({ invoice, onView, onEdit }) => (
  <tr className="hover:bg-gray-50 transition-colors">
    <td className="px-6 py-4 whitespace-nowrap">
      <span className="text-sm font-medium text-[#111827]">{invoice.number}</span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div>
        <p className="text-sm font-medium text-[#111827]">{invoice.client}</p>
        <p className="text-xs text-[#6B7280]">{invoice.clientId}</p>
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <span className="text-sm text-[#111827]">{invoice.date}</span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <span className="text-sm text-[#111827]">{invoice.dueDate}</span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <span className="text-sm font-semibold text-[#111827]">{invoice.amount}</span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <StatusBadge status={invoice.status} />
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-right">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => onView(invoice)}
          className="p-1.5 text-[#6B7280] hover:text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={() => onEdit(invoice)}
          className="p-1.5 text-[#6B7280] hover:text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
        >
          <Edit className="w-4 h-4" />
        </button>
      </div>
    </td>
  </tr>
);

// Componente de empleado
const EmployeeCard = ({ employee }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-shadow">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
        <span className="text-lg font-semibold text-[#3B82F6]">
          {employee.name.split(' ').map(n => n[0]).join('')}
        </span>
      </div>
      <div className="flex-1">
        <h3 className="font-medium text-[#111827]">{employee.name}</h3>
        <p className="text-sm text-[#6B7280]">{employee.position}</p>
        <div className="flex items-center gap-4 mt-2 text-xs text-[#6B7280]">
          <span className="flex items-center gap-1">
            <Building className="w-3 h-3" />
            {employee.department}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Desde {employee.startDate}
          </span>
        </div>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        employee.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'
      }`}>
        {employee.status === 'active' ? 'Activo' : 'Inactivo'}
      </span>
    </div>
  </div>
);

export default function AdministracionPanel() {
  const { user, isAtLeast } = useAuth();
  const [activeTab, setActiveTab] = useState('facturas');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  // Datos de ejemplo
  const [invoices] = useState([
    { id: 1, number: 'FAC-2026-001', client: 'Restaurante El Molino', clientId: 'CLI-001', date: '15/01/2026', dueDate: '15/02/2026', amount: '€1.250,00', status: 'paid' },
    { id: 2, number: 'FAC-2026-002', client: 'Hotel Costa Brava', clientId: 'CLI-002', date: '14/01/2026', dueDate: '14/02/2026', amount: '€3.450,00', status: 'pending' },
    { id: 3, number: 'FAC-2026-003', client: 'Bar Central', clientId: 'CLI-003', date: '10/01/2026', dueDate: '10/02/2026', amount: '€890,00', status: 'overdue' },
    { id: 4, number: 'FAC-2026-004', client: 'Cafeteria Plaza', clientId: 'CLI-004', date: '08/01/2026', dueDate: '08/02/2026', amount: '€2.100,00', status: 'paid' },
    { id: 5, number: 'FAC-2026-005', client: 'Pizzeria Roma', clientId: 'CLI-005', date: '05/01/2026', dueDate: '05/02/2026', amount: '€1.680,00', status: 'draft' }
  ]);

  const [employees] = useState([
    { id: 1, name: 'Carlos Garcia', position: 'Tecnico SAT', department: 'SAT', startDate: 'Mar 2024', status: 'active' },
    { id: 2, name: 'Maria Lopez', position: 'Comercial', department: 'Comercial', startDate: 'Jun 2023', status: 'active' },
    { id: 3, name: 'Pedro Martinez', position: 'Administracion', department: 'Admin', startDate: 'Ene 2022', status: 'active' },
    { id: 4, name: 'Ana Fernandez', position: 'Soporte', department: 'SAT', startDate: 'Sep 2024', status: 'active' }
  ]);

  const [stats] = useState({
    totalFacturado: '€127.450',
    pendienteCobro: '€23.400',
    empleados: 24,
    nominasMes: '€45.600'
  });

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inv.number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Administracion</h1>
          <p className="text-[#6B7280] mt-1">Gestion de facturas, contabilidad y RRHH</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
          <Plus className="w-4 h-4" />
          <span>Nueva factura</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Euro className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Total facturado</p>
              <p className="text-xl font-semibold text-[#111827]">{stats.totalFacturado}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Pendiente cobro</p>
              <p className="text-xl font-semibold text-[#111827]">{stats.pendienteCobro}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Empleados</p>
              <p className="text-xl font-semibold text-[#111827]">{stats.empleados}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Nominas mes</p>
              <p className="text-xl font-semibold text-[#111827]">{stats.nominasMes}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#3B82F6] text-[#3B82F6]'
                  : 'border-transparent text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'facturas' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Buscar facturas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="paid">Pagadas</option>
              <option value="overdue">Vencidas</option>
              <option value="draft">Borradores</option>
            </select>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm hover:bg-gray-50">
              <Download className="w-4 h-4" />
              Exportar
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">Numero</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">Vencimiento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">Importe</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredInvoices.map(invoice => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    onView={(inv) => console.log('View', inv)}
                    onEdit={(inv) => console.log('Edit', inv)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'rrhh' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Buscar empleados..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#2563EB] transition-colors">
              <Plus className="w-4 h-4" />
              <span>Nuevo empleado</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {employees.map(employee => (
              <EmployeeCard key={employee.id} employee={employee} />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'contabilidad' && (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <Euro className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#111827] mb-2">Contabilidad</h3>
          <p className="text-[#6B7280]">Modulo de contabilidad en desarrollo</p>
        </div>
      )}

      {activeTab === 'nominas' && (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <CreditCard className="w-12 h-12 text-[#9CA3AF] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#111827] mb-2">Nominas</h3>
          <p className="text-[#6B7280]">Modulo de nominas en desarrollo</p>
        </div>
      )}
    </div>
  );
}
